import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { Pencil, Plus, Trash2 } from 'lucide-react'

import type { TeaPackage } from '@/lib/api/tea-packages-api'
import type { TeaPackageFormValues } from '@/lib/schemas/tea-package-schemas'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import { useAromaSamplings } from '@/hooks/use-aroma-samplings-query'
import { useUsers } from '@/hooks/use-user-query'
import {
    useCreateTeaPackage,
    useDeleteTeaPackage,
    useTeaPackages,
    useUpdateTeaPackage,
} from '@/hooks/use-tea-packages-query'
import { m } from '@/lib/paraglide/messages'
import { createTeaPackageSchema } from '@/lib/schemas/tea-package-schemas'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/agriculture/tea-packages',
)({
    component: TeaPackagesPage,
})

function TeaPackagesPage() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('tea_packages.write')
    const canDelete = hasPermission('tea_packages.delete')

    const [editing, setEditing] = useState<TeaPackage | null>(null)
    const [creating, setCreating] = useState(false)

    const { data } = useTeaPackages({ per_page: 100 })
    const rows = data?.data ?? []

    const createMut = useCreateTeaPackage()
    const updateMut = useUpdateTeaPackage(editing?.id ?? '')
    const deleteMut = useDeleteTeaPackage()

    const schema = createTeaPackageSchema()
    const form = useForm<TeaPackageFormValues>({
        resolver: zodResolver(schema),
        defaultValues: {
            package_date: '',
            aroma_sampling_id: '',
            weight: 0,
            is_approved: undefined,
            is_active: true,
            implementer_ids: [],
        },
    })

    const isOpen = creating || editing !== null

    function openCreate() {
        form.reset({
            package_date: new Date().toISOString().slice(0, 10),
            aroma_sampling_id: undefined,
            weight: 0,
            is_approved: undefined,
            is_active: true,
            implementer_ids: [],
        })
        setCreating(true)
    }

    function openEdit(pkg: TeaPackage) {
        form.reset({
            package_date: pkg.package_date.slice(0, 10),
            aroma_sampling_id: pkg.aroma_sampling_id ?? undefined,
            weight: pkg.weight,
            is_approved: pkg.is_approved ?? undefined,
            is_active: pkg.is_active,
            implementer_ids: pkg.implementers.map((i) => i.user_id),
        })
        setEditing(pkg)
    }

    function closeDialog() {
        setCreating(false)
        setEditing(null)
        form.reset()
    }

    function onSubmit(values: TeaPackageFormValues) {
        const payload = {
            package_date: values.package_date,
            aroma_sampling_id: values.aroma_sampling_id || null,
            weight: values.weight,
            is_approved: values.is_approved ?? null,
            is_active: values.is_active,
            implementer_ids: values.implementer_ids,
        }
        if (editing) {
            updateMut.mutate(payload, { onSuccess: closeDialog })
        } else {
            createMut.mutate(payload, { onSuccess: closeDialog })
        }
    }

    function handleDelete(pkg: TeaPackage) {
        if (!confirm(m.tea_package_delete_confirm())) return
        deleteMut.mutate(pkg.id)
    }

    const isSaving = createMut.isPending || updateMut.isPending

    return (
        <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold">
                    {m.tea_package_title()}
                </h1>
                {canWrite && (
                    <Button onClick={openCreate}>
                        <Plus className="mr-2 h-4 w-4" />
                        {m.tea_package_new()}
                    </Button>
                )}
            </div>

            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>{m.tea_package_date()}</TableHead>
                        <TableHead>{m.tea_package_weight()}</TableHead>
                        <TableHead>{m.tea_package_is_approved()}</TableHead>
                        <TableHead>{m.tea_package_implementers()}</TableHead>
                        <TableHead />
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((pkg) => (
                        <TableRow key={pkg.id}>
                            <TableCell>
                                {pkg.package_date.slice(0, 10)}
                            </TableCell>
                            <TableCell>{pkg.weight}</TableCell>
                            <TableCell>
                                {pkg.is_approved === null ||
                                pkg.is_approved === undefined ? (
                                    '—'
                                ) : (
                                    <Badge
                                        variant={
                                            pkg.is_approved
                                                ? 'default'
                                                : 'secondary'
                                        }
                                    >
                                        {pkg.is_approved
                                            ? m.common_yes()
                                            : m.common_no()}
                                    </Badge>
                                )}
                            </TableCell>
                            <TableCell>
                                {pkg.implementers.length > 0 ? (
                                    <Badge variant="secondary">
                                        {pkg.implementers.length}
                                    </Badge>
                                ) : (
                                    '—'
                                )}
                            </TableCell>
                            <TableCell className="text-right space-x-1">
                                {canWrite && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => openEdit(pkg)}
                                    >
                                        <Pencil className="h-4 w-4" />
                                    </Button>
                                )}
                                {canDelete && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleDelete(pkg)}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {/* Create / Edit dialog */}
            <TeaPackageFormDialog
                open={isOpen}
                title={editing ? m.tea_package_edit() : m.tea_package_new()}
                form={form}
                onSubmit={onSubmit}
                isSaving={isSaving}
                onClose={closeDialog}
            />
        </div>
    )
}

function TeaPackageFormDialog({
    open,
    title,
    form,
    onSubmit,
    isSaving,
    onClose,
}: {
    open: boolean
    title: string
    form: ReturnType<typeof useForm<TeaPackageFormValues>>
    onSubmit: (v: TeaPackageFormValues) => void
    isSaving: boolean
    onClose: () => void
}) {
    const { data: aromasData } = useAromaSamplings({ per_page: 200 })
    const aromas = aromasData?.data ?? []

    const { data: usersData } = useUsers({ per_page: 200 })
    const users = useMemo(() => usersData?.data ?? [], [usersData])

    const selectedImplementers = form.watch('implementer_ids')
    const toggleImplementer = (id: string, checked: boolean) => {
        const next = checked
            ? [...selectedImplementers, id]
            : selectedImplementers.filter((x) => x !== id)
        form.setValue('implementer_ids', next)
    }

    return (
        <Dialog
            open={open}
            onOpenChange={(o) => {
                if (!o) onClose()
            }}
        >
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                </DialogHeader>
                <form
                    id="tea-package-form"
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-4"
                >
                    {/* Package date */}
                    <div className="space-y-1">
                        <Label>{m.tea_package_date()}</Label>
                        <Input type="date" {...form.register('package_date')} />
                        {form.formState.errors.package_date && (
                            <p className="text-destructive text-sm">
                                {form.formState.errors.package_date.message}
                            </p>
                        )}
                    </div>

                    {/* Aroma sampling (optional) */}
                    <div className="space-y-1">
                        <Label>{m.tea_package_aroma_sampling()}</Label>
                        <Select
                            value={
                                form.watch('aroma_sampling_id') ?? '__none__'
                            }
                            onValueChange={(v) =>
                                form.setValue(
                                    'aroma_sampling_id',
                                    v === '__none__' ? undefined : v,
                                )
                            }
                        >
                            <SelectTrigger>
                                <SelectValue
                                    placeholder={m.tea_package_aroma_sampling_none()}
                                />
                            </SelectTrigger>
                            <SelectContent>
                                {/* Radix Select crashes on value=""; use sentinel */}
                                <SelectItem value="__none__">
                                    {m.tea_package_aroma_sampling_none()}
                                </SelectItem>
                                {aromas.map((a) => (
                                    <SelectItem key={a.id} value={a.id}>
                                        {a.sampling_date.slice(0, 10)}
                                        {a.code ? ` — ${a.code}` : ''}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Weight */}
                    <div className="space-y-1">
                        <Label>{m.tea_package_weight()}</Label>
                        <Input
                            type="number"
                            step="0.01"
                            min="0"
                            {...form.register('weight')}
                        />
                    </div>

                    {/* Implementers */}
                    <div className="space-y-1">
                        <Label>{m.tea_package_implementers()}</Label>
                        <div className="max-h-32 space-y-1 overflow-y-auto rounded-md border p-2">
                            {users.map((u) => (
                                <label
                                    key={u.id}
                                    className="flex items-center gap-2 text-sm"
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedImplementers.includes(
                                            u.id,
                                        )}
                                        onChange={(e) =>
                                            toggleImplementer(
                                                u.id,
                                                e.target.checked,
                                            )
                                        }
                                    />
                                    {u.full_name}
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Is approved */}
                    <div className="flex items-center gap-2">
                        <Switch
                            checked={form.watch('is_approved') === true}
                            onCheckedChange={(v) =>
                                form.setValue('is_approved', v)
                            }
                        />
                        <Label>{m.tea_package_is_approved()}</Label>
                    </div>

                    {/* Is active */}
                    <div className="flex items-center gap-2">
                        <Switch
                            checked={form.watch('is_active')}
                            onCheckedChange={(v) =>
                                form.setValue('is_active', v)
                            }
                        />
                        <Label>{m.common_is_active()}</Label>
                    </div>
                </form>
                <DialogFooter>
                    <Button
                        form="tea-package-form"
                        type="submit"
                        disabled={isSaving}
                    >
                        {m.tea_package_save()}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
